// src/screens/customer/customerPannel/dashboard/Dashboard.jsx

import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Package,
  Heart,
  ShoppingCart,
  Gavel,
  ShoppingBag,
  Truck,
  Headphones,
  BookMarked,
} from "lucide-react";
import "../../../../assets/css/customer/customerPannel/dashboard/Dashboard.css";
import AppContext from "../../../../context/AppContext";

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "delivered":
      return "#16a34a";
    case "in transit":
      return "#d97706";
    case "processing":
      return "#2563eb";
    case "pending":
      return "#6b7280";
    default:
      return "#6b7280";
  }
};

const formatDate = (isoDate) => {
  if (!isoDate) return "—";
  const d = new Date(isoDate);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};


const Dashboard = () => {
  const { loggedInCustomer, baseUrl, authToken } = useContext(AppContext);
  const location = useLocation();

  const [dashboardStats, setDashboardStats] = useState({
    totalOrders: 0,
    wishlistItems: 0,
    cartItems: 0,
    totalBids: 0,
  });

  const [recentOrders, setRecentOrders] = useState([]);

  // Dashboard stats
  useEffect(() => {
    if (!loggedInCustomer) return; // ← null check
    const formData = new FormData();
    formData.append("adcustomer_id", loggedInCustomer?.id);
    axios
      .post(`${baseUrl}/customer/dashboard/stats`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        console.log("DASHBOARD STATS:", res.data);
        if (res.data.success) {
          setDashboardStats({
            totalOrders: res.data.totalOrders,
            wishlistItems: res.data.wishlistItems,
            cartItems: res.data.cartItems,
            totalBids: res.data.totalBids,
          });
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [baseUrl, location.pathname, authToken, loggedInCustomer]); // ← loggedInCustomer added

  // Recent orders
  useEffect(() => {
    if (!loggedInCustomer) return; // ← null check
    const formData = new FormData();
    formData.append("adcustomer_id", loggedInCustomer.id);
    axios
      .post(`${baseUrl}/customer/dashboard/recentOrders`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        console.log("RECENT ORDERS:", res.data);
        if (res.data.success) {
          setRecentOrders(res.data.orders);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [baseUrl, location.pathname, authToken, loggedInCustomer]); // ← loggedInCustomer added

  const stats = [
    {
      icon: Package,
      label: "Total Orders",
      value: dashboardStats.totalOrders,
      color: "#5C6B47",
      bg: "rgba(92,107,71,0.1)",
    },
    {
      icon: Heart,
      label: "Wishlist Items",
      value: dashboardStats.wishlistItems,
      color: "#D67E43",
      bg: "rgba(214,126,67,0.1)",
    },
    {
      icon: ShoppingCart,
      label: "Cart Items",
      value: dashboardStats.cartItems,
      color: "#c7a83a",
      bg: "rgba(199,168,58,0.1)",
    },
    {
      icon: Gavel,
      label: "Total Bids",
      value: dashboardStats.totalBids,
      color: "#6B4C3B",
      bg: "rgba(107,76,59,0.1)",
    },
  ];

  const quickActions = [
    {
      icon: ShoppingBag,
      label: "Browse Products",
      to: "/products",
      color: "#D67E43",
      bg: "rgba(214,126,67,0.08)",
    },
    {
      icon: Truck,
      label: "My Orders",
      to: "/customer/orders",
      color: "#5C6B47",
      bg: "rgba(92,107,71,0.08)",
    },
    {
      icon: BookMarked,
      label: "Wishlist",
      to: "/customer/wishlist",
      color: "#c7a83a",
      bg: "rgba(199,168,58,0.08)",
    },
    {
      icon: Headphones,
      label: "Help & Support",
      to: "/customer/help-support",
      color: "#6B4C3B",
      bg: "rgba(107,76,59,0.08)",
    },
  ];

  return (
    <div className="dash-page">
      {/* Welcome Banner */}
      <div className="dash-welcome">
        <div className="dash-avatar">
          {loggedInCustomer?.fullName?.charAt(0).toUpperCase() || "C"}
        </div>
        <div className="dash-welcome-text">
          <h1 className="dash-welcome-title">
            Welcome back,{" "}
            <span>
              {loggedInCustomer?.fullName?.split(" ")[0] || "Customer"}
            </span>
          </h1>
          <p className="dash-welcome-sub">
            Here's what's happening with your account today.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="dash-stats-grid">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              className="dash-stat-card"
              key={i}
              style={{ "--accent": s.color, "--accent-bg": s.bg }}
            >
              <div className="dash-stat-icon">
                <Icon size={22} strokeWidth={1.8} color={s.color} />
              </div>
              <div>
                <div className="dash-stat-value">{s.value}</div>
                <div className="dash-stat-label">{s.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Orders */}
      <div className="dash-card">
        <div className="dash-card-header">
          <h2 className="dash-card-title">Recent Orders</h2>
          <Link to="/customer/orders" className="dash-link-btn">
            View All →
          </Link>
        </div>
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Date</th>
                <th>Item</th>
                <th>Status</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "20px" }}>
                    No orders yet
                  </td>
                </tr>
              ) : (
                recentOrders.map((o) => (
                  <tr key={o.id}>
                    <td className="dash-order-id">ORD-{String(o.id).padStart(4, "0")}</td>
                    <td>{formatDate(o.created_at)}</td>
                    <td>
                      {o.adcart && o.adcart.length > 0
                        ? o.adcart[0].adproduct?.name
                        : "—"}
                    </td>
                    <td>
                      <span
                        className="dash-badge"
                        style={{ "--bc": getStatusColor(o.status) }}
                      >
                        {o.status}
                      </span>
                    </td>
                    <td className="dash-amount">
                      Rs. {Number(o.total_price).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="dash-card">
        <div className="dash-card-header">
          <h2 className="dash-card-title">Quick Actions</h2>
        </div>
        <div className="dash-actions-grid">
          {quickActions.map((a, i) => {
            const Icon = a.icon;
            return (
              <Link
                to={a.to}
                className="dash-action-card"
                key={i}
                style={{ "--qa-bg": a.bg }}
              >
                <div
                  className="dash-action-icon-wrap"
                  style={{ background: a.bg }}
                >
                  <Icon size={24} strokeWidth={1.8} color={a.color} />
                </div>
                <span className="dash-action-label">{a.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;